import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import {
  UsersIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ChartBarIcon,
  XMarkIcon,
  UserCircleIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const CompanyCustomersPage = () => {
  const { t } = useTranslation();
  const { user, apiCall } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await apiCall(
        `/companies/${user?.company_id}/customers`,
      );
      if (response.success) {
        setCustomers(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerHistory = async (customerId) => {
    try {
      setHistoryLoading(true);
      const response = await apiCall(
        `/companies/${user?.company_id}/customers/${customerId}/history`,
      );
      if (response.success) {
        setCustomerHistory(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching customer history:", error);
      setCustomerHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
    await fetchCustomerHistory(customer.user_id);
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedCustomer(null);
    setCustomerHistory([]);
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm),
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("ru-RU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case t("company.customers.status.completed"):
        return "bg-emerald-50 text-emerald-700 border-emerald-100";
      case t("company.customers.status.confirmed"):
        return "bg-blue-50 text-blue-700 border-blue-100";
      case t("company.customers.status.pending"):
        return "bg-amber-50 text-amber-700 border-amber-100";
      case t("company.customers.status.cancelleds"):
        return "bg-rose-50 text-rose-700 border-rose-100";
      default:
        return "bg-zinc-50 text-zinc-700 border-zinc-100";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-zinc-100 rounded-2xl w-1/4"></div>
          <div className="h-64 bg-zinc-50 rounded-[32px]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-zinc-900">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <div className="inline-flex items-center justify-center p-2.5 bg-zinc-900 rounded-2xl mb-4 shadow-xl shadow-zinc-200">
              <UsersIcon className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-black tracking-tight text-zinc-900">
              {t("company.customers.title")}
            </h1>
            <p className="text-zinc-500 mt-1 font-medium">
              {t("company.customers.description")}
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-zinc-100 p-1.5 rounded-2xl">
            <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
              {t("company.customers.total")} {customers.length}
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-8 group">
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400 group-focus-within:text-zinc-900 transition-colors" />
          </div>
          <input
            type="text"
            placeholder={t("company.customers.search_placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-12 pr-6 py-4 bg-zinc-50 border-none rounded-[22px] text-zinc-900 placeholder-zinc-400 focus:ring-2 focus:ring-zinc-200 transition-all shadow-sm"
          />
        </div>

        {/* Table Container */}
        <div className="bg-white border border-zinc-100 rounded-[32px] overflow-hidden shadow-sm">
          {filteredCustomers.length === 0 ? (
            <div className="py-20 text-center">
              <div className="inline-flex p-6 bg-zinc-50 rounded-full mb-4">
                <UsersIcon className="w-10 h-10 text-zinc-300" />
              </div>
              <h3 className="text-lg font-bold text-zinc-900">
                {t("company.customers.table.empty_state")}
              </h3>
              <p className="text-zinc-500 mt-1">
                {t("company.customers.table.empty_state_subtitle")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-100">
                <thead>
                  <tr className="bg-zinc-50/50">
                    <th className="px-8 py-5 text-left text-xs font-bold text-zinc-400 uppercase tracking-widest">
                      {t("company.customers.table.header_client")}
                    </th>
                    <th className="px-8 py-5 text-left text-xs font-bold text-zinc-400 uppercase tracking-widest">
                      {t("company.customers.table.header_contacts")}
                    </th>
                    <th className="px-8 py-5 text-left text-xs font-bold text-zinc-400 uppercase tracking-widest">
                      {t("company.customers.table.header_address")}
                    </th>
                    <th className="px-8 py-5 text-right text-xs font-bold text-zinc-400 uppercase tracking-widest">
                      {t("company.customers.table.header_action")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {filteredCustomers.map((customer) => (
                    <tr
                      key={customer.user_id}
                      className="hover:bg-zinc-50/80 transition-colors group"
                    >
                      <td className="px-8 py-6">
                        <div className="flex items-center">
                          <div className="h-12 w-12 rounded-[18px] bg-zinc-100 flex items-center justify-center border border-zinc-200 group-hover:bg-white transition-colors">
                            <UserCircleIcon className="h-7 w-7 text-zinc-400" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-bold text-zinc-900">
                              {customer.first_name} {customer.last_name}
                            </div>
                            <div className="text-[12px] text-zinc-400 font-medium mt-0.5">
                              {customer.gender && (
                                <span className="capitalize">
                                  {customer.gender}
                                </span>
                              )}
                              {customer.date_of_birth && (
                                <span>
                                  {" "}
                                  • {formatDate(customer.date_of_birth)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center text-sm text-zinc-600 font-medium">
                            <EnvelopeIcon className="h-3.5 w-3.5 mr-2 text-zinc-300" />
                            {customer.email}
                          </div>
                          <div className="flex items-center text-sm text-zinc-600 font-medium">
                            <PhoneIcon className="h-3.5 w-3.5 mr-2 text-zinc-300" />
                            {customer.phone}
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-start text-sm text-zinc-500 leading-tight">
                          <MapPinIcon className="h-4 w-4 mr-2 text-zinc-300 mt-0.5" />
                          <div className="font-medium text-[13px]">
                            {customer.address && <div>{customer.address}</div>}
                            <div>
                              {customer.city}, {customer.country}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <button
                          onClick={() => handleViewCustomer(customer)}
                          className="inline-flex items-center justify-center px-4 py-2 bg-zinc-100 hover:bg-zinc-900 hover:text-white text-zinc-900 text-xs font-bold rounded-xl transition-all active:scale-95"
                        >
                          {t("company.customers.table.button_details")}
                          <ChevronRightIcon
                            className="h-3 w-3 ml-1.5"
                            strokeWidth={3}
                          />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modern Modal */}
      {showDetailsModal && selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm"
            onClick={closeModal}
          ></div>
          <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl rounded-[32px] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-8 py-6 border-b border-zinc-50">
              <div>
                <h3 className="text-xl font-black text-zinc-900">
                  {t("company.customers.modal.title")}
                </h3>
                <p className="text-zinc-400 text-sm font-medium">
                  {selectedCustomer.first_name} {selectedCustomer.last_name}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-zinc-400" />
              </button>
            </div>

            <div className="overflow-y-auto p-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Info Sidebar */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-zinc-50 p-6 rounded-[24px] border border-zinc-100">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.1em] text-zinc-400 mb-4">
                    {t("company.customers.modal.info_section")}
                  </h4>
                  <div className="space-y-4">
                    <DetailItem
                      label={t("company.customers.modal.label_email")}
                      value={selectedCustomer.email}
                    />
                    <DetailItem
                      label={t("company.customers.modal.label_phone")}
                      value={selectedCustomer.phone}
                    />
                    <DetailItem
                      label={t("company.customers.modal.label_dob")}
                      value={formatDate(selectedCustomer.date_of_birth)}
                    />
                    <DetailItem
                      label={t("company.customers.modal.label_address")}
                      value={`${selectedCustomer.address || ""} ${selectedCustomer.city || ""}`}
                    />
                  </div>
                </div>

                {selectedCustomer.emergency_contact_name && (
                  <div className="bg-rose-50/50 p-6 rounded-[24px] border border-rose-100">
                    <h4 className="text-[11px] font-black uppercase tracking-[0.1em] text-rose-400 mb-4">
                      {t("company.customers.modal.emergensy_section")}
                    </h4>
                    <div className="text-sm font-bold text-rose-900">
                      {selectedCustomer.emergency_contact_name}
                    </div>
                    <div className="text-xs text-rose-600 font-medium mt-1">
                      {selectedCustomer.emergency_contact_phone}
                    </div>
                  </div>
                )}
              </div>

              {/* Booking History Main */}
              <div className="lg:col-span-3">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-bold text-zinc-900 flex items-center">
                    <ChartBarIcon className="h-4 w-4 mr-2 text-zinc-400" />
                    {t("company.customers.modal.history_empty")}
                  </h4>
                  <span className="px-3 py-1 bg-zinc-100 rounded-full text-[10px] font-black text-zinc-500 uppercase tracking-wider">
                    {t("company.customers.total")} {customerHistory.length}
                  </span>
                </div>

                <div className="space-y-3">
                  {historyLoading ? (
                    [1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-20 bg-zinc-50 animate-pulse rounded-2xl"
                      ></div>
                    ))
                  ) : customerHistory.length === 0 ? (
                    <div className="text-center py-10 bg-zinc-50 rounded-[24px] border border-dashed border-zinc-200">
                      <p className="text-zinc-400 text-sm font-medium">
                        {t("company.customers.modal.history_empty")}
                      </p>
                    </div>
                  ) : (
                    customerHistory.map((booking) => (
                      <div
                        key={booking.id}
                        className="group p-4 bg-white border border-zinc-100 rounded-[20px] hover:shadow-md transition-all"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-xs font-black text-zinc-400 uppercase tracking-tighter mb-1">
                              {formatDate(booking.date_time)}
                            </div>
                            <div className="text-sm font-bold text-zinc-900">
                              {booking.service_name ||
                                t("company.customers.modal.default_service")}
                            </div>
                            <div className="text-xs text-zinc-500 font-medium mt-1">
                              {t("company.customers.modal.label_pet")}{" "}
                              {booking.pet_name || "—"}
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            <span
                              className={`px-2.5 py-1 text-[10px] font-black uppercase rounded-lg border ${getStatusStyle(booking.status)}`}
                            >
                              {booking.status}
                            </span>
                            <div className="mt-2 text-sm font-black text-zinc-900">
                              {formatCurrency(booking.price)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательный компонент для деталей
const DetailItem = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
      {label}
    </div>
    <div className="text-sm font-bold text-zinc-800 break-words">
      {value || "—"}
    </div>
  </div>
);

export default CompanyCustomersPage;
